import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { getLocalStorageData, STORAGE_KEY } from 'services/localStorage.service'
import { useStore } from 'store/useStore'
import { PickPointType } from 'sections/LKSection/shared/PickPointSharedModule/types'
import { Margin } from 'components/ui'
import { ReactSelect } from 'components/ui/Select/ReactSelect'
import { ErrorText } from 'components/ui/Text/Text'
import { Checkbox, Flex, Form, FormItem, Text, TextArea, Title } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { useResize } from 'utils/hooks/useResize'
import { colors } from 'shared/constants/values'
import { BaseOption, Country } from 'shared/types/types'

import { countriesWithSubsidy, couriersAccessItems, OrderKeys, pickPointTypeOptions } from '../../../data'
import { OrderLocationViewModel } from '../../../stores/models/OrderLocationViewModel'
import { CouriersAccess, OrderStatus, PickPointLocation, SubsidyHexType } from '../../../types'
import { OrdersMap } from '../../OrdersMap/OrdersMap'
import { OrderAddress } from './Address/OrderAddress'
import { PickPointTypeTooltip } from './PickPointTypeTooltip/PickPointTypeTooltip'
import { SubsidyTypeInfo } from './SubsidyTypeInfo/SubsidyTypeInfo'

type LocationFormType = {
  description: string
  courierWay: string
  pickPointType: BaseOption<PickPointType> | null
  couriersAccess: CouriersAccess
}

type OrderLocationFormProps = {
  uiStore: OrderLocationViewModel
  country: Country
  isMoving: boolean
  isCreation?: boolean
  disabled?: boolean
}

const getKey = (key: string) => `${OrderKeys.locationStep}.${key}`

export const OrderLocationForm = observer(
  ({ uiStore, country, isMoving, isCreation = false, disabled = false }: OrderLocationFormProps) => {
    const { viewOrderUiStore, viewPvzUiStore } = useStore()

    const pickPointTypeItem = pickPointTypeOptions.find((item) => item.value === uiStore.pickPointType)

    const { control, setValue } = useForm<LocationFormType>({
      mode: 'onChange',
      defaultValues: {
        description: uiStore.description,
        courierWay: uiStore.courierWay,
        pickPointType: pickPointTypeItem,
        couriersAccess: uiStore.couriersAccess,
      },
    })

    const { isTablet } = useResize()
    const { t } = useTranslation()

    const location = useLocation()

    const queryParams = new URLSearchParams(location.search)
    const exPvzId = queryParams.get('pvzId')

    const status = viewOrderUiStore.selectedOrder?.status
    const isShoppingMall = uiStore.floorLevel === PickPointLocation.shoppingMall
    const isOrderDraft = status === OrderStatus.draft

    const isSubsidyTypeDisabled =
      disabled ||
      (!isCreation && status !== OrderStatus.draft) ||
      uiStore.subsidyInfo.length === 0 ||
      (uiStore.pickPointType !== PickPointType.partner && country !== Country.GE) ||
      uiStore.streetItem === null

    const isCouriersAccessVisible =
      (country === Country.RU || country === Country.BY) &&
      !isCreation &&
      uiStore.floorLevel === PickPointLocation.shoppingMall

    useAsyncEffect(async () => {
      if (isCreation && uiStore.coordinates.latitude && uiStore.coordinates.longitude) {
        const pickPointType = getLocalStorageData(STORAGE_KEY.PICK_POINT_TYPE) as PickPointType
        const pickPointTypeItem = pickPointTypeOptions.find((item) => item.value === pickPointType)
        const subsidyHexType = getLocalStorageData(STORAGE_KEY.SUBSIDY_HEX_TYPE) as SubsidyHexType

        if (pickPointType) {
          setValue('pickPointType', pickPointTypeItem!)
          uiStore.setPickPointType(pickPointType)
        }

        if (subsidyHexType && !uiStore.subsidyHexTypeItem && pickPointType !== PickPointType.folk) {
          await uiStore.getSubsidy(5)

          const subsidyItem = uiStore.subsidyInfo.find((subsidy) => subsidy.subsidyType === subsidyHexType)

          if (subsidyItem) {
            uiStore.setSubsidyHexTypeItem({
              value: subsidyItem.subsidyType,
              label: t(`${OrderKeys.orderModule}.${subsidyItem.subsidyType}`),
            })
          }
        }
      }
    }, [uiStore.coordinates.latitude, uiStore.coordinates.longitude])

    useEffect(() => {
      if (!isCreation && viewOrderUiStore.initialized) {
        setValue('description', uiStore.description)
        setValue('courierWay', uiStore.courierWay)
        setValue('pickPointType', pickPointTypeItem!)
        setValue('couriersAccess', uiStore.couriersAccess)
      }
    }, [viewOrderUiStore.initialized])

    useEffect(() => {
      if (uiStore.pickPointType === PickPointType.folk) {
        uiStore.setSubsidyHexTypeItem(null)
      }
    }, [uiStore.pickPointType, uiStore])

    useEffect(() => {
      if (isMoving && exPvzId) {
        const pvzItem = viewPvzUiStore.getPvz(+exPvzId)

        if (pvzItem?.typePoint) {
          uiStore.setPickPointType(pvzItem?.typePoint)
        }
      }
    }, [])

    return (
      <Flex vertical>
        <Title level={isCreation ? 3 : 5}>{t(getKey('Расположение ПВЗ'))}</Title>

        {isCreation && (
          <Margin my={24}>
            <Title level={4}>{t(getKey('Открытие ПВЗ'))}</Title>
          </Margin>
        )}

        {/* mytodo: заменить react-hook-form на форму из анта */}

        <Form>
          {country === Country.RU && !isMoving && (
            <FormItem label={t(getKey('Выбор типа ПВЗ'))} tooltip={<PickPointTypeTooltip />}>
              <Controller<LocationFormType>
                name={'pickPointType'}
                control={control}
                render={({ field }) => (
                  <ReactSelect
                    value={field.value as PickPointType}
                    options={pickPointTypeOptions.map((item) => ({ value: item.value, label: t(getKey(item.label)) }))}
                    placeholder={t(getKey('Выберите тип ПВЗ'))}
                    isDisabled={disabled || !isCreation || isOrderDraft}
                    onChange={(item) => {
                      uiStore.setPickPointType((item as BaseOption<PickPointType>).value)
                      field.onChange(item as BaseOption<PickPointType>)
                    }}
                    className="pickPointTypeItem"
                  />
                )}
              />
            </FormItem>
          )}

          <OrderAddress uiStore={uiStore} isCreation={isCreation} country={country} />

          {isTablet && isCreation && (
            <FormItem>
              <OrdersMap
                height={400}
                centerLatitude={uiStore.streetItem?.lat}
                centerLongitude={uiStore.streetItem?.lng}
              />
            </FormItem>
          )}

          {countriesWithSubsidy.includes(country) && !isMoving && (
            <FormItem label={t(getKey('Тип субсидии'))}>
              {!isSubsidyTypeDisabled && (
                <Margin mb={8}>
                  <SubsidyTypeInfo type={isTablet ? 'collapse' : 'dropdown'} />
                </Margin>
              )}

              <ReactSelect
                value={uiStore.getSubsidyItem(country)}
                onChange={(item) => uiStore.setSubsidyHexTypeItem(item as BaseOption<SubsidyHexType>)}
                options={uiStore.getSubsidyTypeOptions(country)}
                isDisabled={isSubsidyTypeDisabled}
                isLoading={uiStore.isSubsidyLoading}
                placeholder={t(getKey('Выберите тип субсидии'))}
                className="subsidyHexTypeItem"
              />
            </FormItem>
          )}

          <FormItem label={t(getKey('Как найти ПВЗ'))}>
            <Controller<LocationFormType>
              name={'description'}
              control={control}
              rules={{
                required: t(getSharedKey('required')),
                minLength: { value: 30, message: t(getSharedKey('minLength'), { count: 30 }) },
                maxLength: { value: 200, message: t(getSharedKey('maxLength'), { count: 200 }) },
                validate: {
                  noWhiteSpace: (value) =>
                    (value as string).trim().length > 29 || t(getSharedKey('minLength'), { count: 30 }),
                },
              }}
              render={({ field, fieldState }) => (
                <>
                  <TextArea
                    value={field.value as string}
                    autoSize
                    onChange={(e) => {
                      field.onChange(e.target.value)
                      uiStore.setDescription(e.target.value)
                    }}
                    maxLength={500}
                    placeholder={t(getKey('Нюансы расположения ПВЗ'))}
                    disabled={disabled}
                    name={'description'}
                    data-testid="description"
                  />
                  {fieldState.error?.message && <ErrorText error>{fieldState.error.message}</ErrorText>}
                </>
              )}
            />
          </FormItem>

          <FormItem
            label={`${t(getKey('Информация для курьеров'))}${
              isShoppingMall ? '' : ` (${t(getSharedKey('notRequired'))})`
            }`}
            tooltip={t(getKey('courierWayTooltip'))}
          >
            <Controller<LocationFormType>
              name={'courierWay'}
              control={control}
              rules={{
                required: isShoppingMall,
                maxLength: { value: 256, message: t(getSharedKey('maxLength'), { count: 256 }) },
              }}
              render={({ field, fieldState }) => (
                <>
                  <TextArea
                    value={field.value as string}
                    autoSize
                    onChange={(e) => {
                      field.onChange(e.target.value)
                      uiStore.setCourierWay(e.target.value)
                    }}
                    maxLength={500}
                    placeholder={t(getKey('Информация для курьера'))}
                    name={'courierWay'}
                    disabled={disabled}
                    data-testid="courierWay"
                  />
                  {fieldState.error?.message && <ErrorText error>{fieldState.error.message}</ErrorText>}
                </>
              )}
            />
          </FormItem>

          {isCouriersAccessVisible && (
            <FormItem
              label={t(getKey('Доступ в ТЦ для курьеров'))}
              tooltip={
                <Flex vertical gap={4}>
                  <Text style={{ color: colors.white }}>{t(getKey('Только ночной доступ'))}</Text>
                  <Text style={{ color: colors.white }}>{t(getKey('Круглосуточный доступ'))}</Text>
                  <Text style={{ color: colors.white }}>{t(getKey('Только дневной доступ'))}</Text>
                </Flex>
              }
            >
              <Flex vertical>
                {couriersAccessItems.map((item) => (
                  <Controller<LocationFormType>
                    key={item}
                    name={'couriersAccess'}
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        key={item}
                        checked={item === uiStore.couriersAccess}
                        onChange={() => {
                          field.onChange(item)
                          uiStore.setCouriersAccess(item)
                        }}
                        data-testid="couriersAccess"
                        disabled={disabled}
                      >
                        <Text>{t(getKey(item))}</Text>
                      </Checkbox>
                    )}
                  />
                ))}
              </Flex>
            </FormItem>
          )}
        </Form>
      </Flex>
    )
  },
)
